import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Heart, Truck, RotateCcw, ShieldCheck, Minus, Plus } from "lucide-react";
import { api, money, track, resolveImg } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Reviews } from "../components/Reviews";
import { useCart } from "../context/CartContext";
import { ShareButtons } from "../components/ShareButtons";

const FALLBACK = "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?crop=entropy&cs=srgb&fm=jpg&q=85";

const APPAREL_ORDER = ["2XS", "XXS", "XS", "S", "SM", "SMALL", "M", "MD", "MED", "MEDIUM", "L", "LG", "LARGE", "XL", "X-LARGE", "XLARGE", "2XL", "XXL", "2X-LARGE", "3XL", "XXXL", "3X-LARGE", "4XL", "4X-LARGE", "5XL", "5X-LARGE", "6XL", "7XL"];
const sizeRank = (val) => {
  const u = String(val).trim().toUpperCase();
  const idx = APPAREL_ORDER.indexOf(u);
  if (idx !== -1) return idx;
  return null;
};
const numericValue = (val) => {
  const s = String(val).trim();
  const frac = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (frac) return parseInt(frac[1], 10) + parseInt(frac[2], 10) / parseInt(frac[3], 10);
  const pure = s.match(/^(\d+(?:\.\d+)?)$/);
  if (pure) return parseFloat(pure[1]);
  return null;
};
const sortSizeValues = (values) => {
  if (values.every((v) => numericValue(v) !== null)) return [...values].sort((a, b) => numericValue(a) - numericValue(b));
  if (values.every((v) => sizeRank(v) !== null)) return [...values].sort((a, b) => sizeRank(a) - sizeRank(b));
  const lead = (v) => { const m = String(v).trim().match(/^(\d+(?:\.\d+)?)/); return m ? parseFloat(m[1]) : null; };
  if (values.every((v) => lead(v) !== null)) return [...values].sort((a, b) => lead(a) - lead(b) || String(a).localeCompare(String(b)));
  return values;
};

function VariationPicker({ variations, selected, onSelect }) {
  const { groups, skus } = variations;
  const valuesFor = (group) => {
    const seen = [];
    for (const s of skus) {
      const val = s.specs?.[group];
      if (val && !seen.includes(val)) seen.push(val);
    }
    return sortSizeValues(seen);
  };
  const valueInStock = (group, value) => skus.some((s) => s.specs?.[group] === value && s.inventory > 0);
  return (
    <div className="space-y-4">
      {groups.filter((group) => valuesFor(group).length > 0).map((group) => (
        <div key={group}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-black/50 mb-2">
            {group}: <span className="text-black">{selected[group] || "—"}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {valuesFor(group).map((value) => {
              const active = selected[group] === value;
              const inStock = valueInStock(group, value);
              return (
                <button key={value} data-testid={`variant-${group.toLowerCase()}-${value.replace(/\W+/g, "-").toLowerCase()}`}
                  onClick={() => onSelect(group, value)}
                  className={`px-3 py-2 text-xs font-semibold border transition-colors duration-150 ${active ? "border-[#4A5D23] bg-[#4A5D23] text-white" : inStock ? "border-black/20 hover:border-black" : "border-black/10 text-black/30 line-through"}`}>
                  {value}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [tab, setTab] = useState("description");
  const [selected, setSelected] = useState({});
  const { addItem, wishlist, toggleWishlist } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    setImgIdx(0); setQty(1); setSelected({});
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      track("product_view", "website", { slug });
      if (data.variations?.skus?.length) {
        const first = data.variations.skus.find((s) => s.inventory > 0) || data.variations.skus[0];
        setSelected({ ...first.specs });
      }
    }).catch(() => setProduct(false));
    api.get(`/products/${slug}/related`).then(({ data }) => setRelated(data)).catch(() => {});
  }, [slug]);

  const v = product?.variations;
  const currentSku = useMemo(() => {
    if (!v) return null;
    return v.skus.find((s) => v.groups.every((g) => s.specs?.[g] === selected[g])) || null;
  }, [v, selected]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = [...(product.images || [])];
    if (currentSku?.image && !imgs.includes(currentSku.image)) imgs.unshift(currentSku.image);
    return imgs;
  }, [product, currentSku]);

  useEffect(() => {
    if (currentSku?.image) {
      const idx = gallery.indexOf(currentSku.image);
      if (idx >= 0) setImgIdx(idx);
    } else if (currentSku) {
      setImgIdx(0);
    }
  }, [currentSku, gallery]);

  if (product === false) return <div className="max-w-7xl mx-auto px-4 py-32 text-center text-black/50">Product not found. <Link to="/shop" className="text-[#4A5D23] underline">Back to shop</Link></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-32 text-center text-black/40 animate-pulse">Loading gear…</div>;

  const onSelectVariant = (group, value) => {
    const next = { ...selected, [group]: value };
    const match = v.skus.find((s) => v.groups.every((g) => s.specs?.[g] === next[g]));
    if (match) setSelected(next);
    else {
      const fallback = v.skus.find((s) => s.specs?.[group] === value);
      if (fallback) setSelected({ ...fallback.specs });
    }
  };

  const saleFactor = product.sale_price && product.price ? product.sale_price / product.price : 1;
  const price = currentSku ? Math.round(currentSku.price * saleFactor * 100) / 100 : (product.sale_price || product.price);
  const originalPrice = currentSku ? currentSku.price : product.price;
  const stock = currentSku ? currentSku.inventory : product.stock;
  const variantLabel = v ? v.groups.map((g) => selected[g]).filter(Boolean).join(" / ") : null;
  const saved = wishlist.includes(product.slug);
  const cartEntry = {
    item_type: "product", slug: product.slug,
    name: v ? `${product.name} (${variantLabel})` : product.name,
    price, image: currentSku?.image || product.images?.[0],
    sku_code: currentSku?.sku_code, variant_label: variantLabel,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <nav className="text-xs text-black/50 mb-6 uppercase tracking-wider">
        <Link to="/" className="hover:text-black">Home</Link> / <Link to="/shop" className="hover:text-black">Shop</Link> / <Link to={`/shop?category=${product.category}`} className="hover:text-black">{product.category.replace(/-/g, " ")}</Link> / <span className="text-black">{product.name}</span>
      </nav>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square bg-[#F4F3F0] border border-black/10 overflow-hidden">
            <img data-testid="product-main-image" src={resolveImg(gallery[imgIdx]) || FALLBACK} alt={product.name}
              onError={(e) => { e.currentTarget.src = FALLBACK; }} className="w-full h-full object-cover" />
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            {gallery.map((img, i) => (
              <button key={img} data-testid={`product-thumb-${i}`} onClick={() => setImgIdx(i)} className={`w-20 h-20 border ${imgIdx === i ? "border-[#4A5D23] border-2" : "border-black/10"}`}>
                <img src={resolveImg(img)} alt={`${product.name} view ${i + 1}`} onError={(e) => { e.currentTarget.src = FALLBACK; }} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex gap-2 mb-3">
            {(product.badges || []).map((b) => (
              <span key={b} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${b === "BEST SELLER" ? "bg-[#121212] text-white" : "bg-[#4A5D23] text-white"}`}>{b}</span>
            ))}
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40">{product.brand || "Shodan Select"}</p>
          <h1 data-testid="product-title" className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight leading-tight mt-1">{product.name}</h1>
          <p className="text-black/60 mt-2">{product.tagline}</p>
          {product.review_count > 0 && (
            <div data-testid="pdp-rating" className="flex items-center gap-2 mt-3 text-[#B8860B]">
              <Star size={16} fill="currentColor" />
              <span className="text-sm font-bold text-black">{product.rating}</span>
              <span className="text-sm text-black/40">({product.review_count} review{product.review_count === 1 ? "" : "s"})</span>
            </div>
          )}
          <div className="mt-5 flex items-baseline gap-3">
            <span data-testid="product-price" className="font-bold text-3xl">{money(price)}</span>
            {saleFactor < 1 && <><span className="text-lg text-black/40 line-through">{money(originalPrice)}</span>
              <span className="text-xs font-bold uppercase tracking-widest bg-[#8B0000] text-white px-2 py-1">Save {money(originalPrice - price)}</span></>}
          </div>
          <p data-testid="product-stock" className={`text-xs font-bold uppercase tracking-widest mt-3 ${stock > 0 ? "text-[#2E8B57]" : "text-[#8B0000]"}`}>
            {stock > 10 ? "In Stock — Ships Fast" : stock > 0 ? `Only ${stock} left` : "Out of Stock"}
          </p>
          {currentSku && <p className="text-[11px] font-mono text-black/40 mt-1">SKU {currentSku.sku_code} · UPC {currentSku.upc}</p>}

          {product.has_restrictions && (
            <div data-testid="restriction-notice" className="mt-4 border border-[#B8860B]/40 bg-[#B8860B]/10 px-4 py-3 text-xs text-black/70">
              <span className="font-bold uppercase tracking-widest text-[#8a6508]">Shipping Restrictions Apply</span> — this item is regulated in some states and localities and cannot be shipped everywhere. Please verify your local laws before ordering; restricted orders may be cancelled and refunded.
            </div>
          )}
          {v && (
            <div className="mt-6 border border-black/10 p-5 bg-[#F4F3F0]/60" data-testid="variation-picker">
              <VariationPicker variations={v} selected={selected} onSelect={onSelectVariant} />
            </div>
          )}

          {(product.good_for || []).length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-black/50 mb-2">Good For</p>
              <div className="flex flex-wrap gap-2">
                {product.good_for.map((g) => (
                  <Link key={g} to={`/shop?good_for=${encodeURIComponent(g)}`} className="text-xs border border-black/20 px-3 py-1.5 hover:border-[#4A5D23] hover:text-[#4A5D23] transition-colors duration-150">{g}</Link>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-black/20">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F4F3F0]" aria-label="Decrease"><Minus size={14} /></button>
              <span data-testid="qty-value" className="w-10 text-center font-bold text-sm">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F4F3F0]" aria-label="Increase"><Plus size={14} /></button>
            </div>
            <button data-testid="wishlist-toggle" onClick={() => toggleWishlist(product.slug)} aria-label="Wishlist"
              className={`p-3 border border-black/20 transition-colors duration-150 ${saved ? "text-[#8B0000]" : "hover:text-[#8B0000]"}`}>
              <Heart size={18} fill={saved ? "currentColor" : "none"} />
            </button>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button data-testid="add-to-cart-btn" disabled={stock <= 0 || (v && !currentSku)}
              onClick={() => { addItem(cartEntry, qty); track("add_to_cart", "website", { slug, sku: currentSku?.sku_code }); }}
              className="flex-1 bg-[#121212] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150 disabled:opacity-40">
              Add to Cart
            </button>
            <button data-testid="buy-now-btn" disabled={stock <= 0 || (v && !currentSku)}
              onClick={() => { addItem(cartEntry, qty); navigate("/checkout"); }}
              className="flex-1 bg-[#4A5D23] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150 disabled:opacity-40">
              Buy Now
            </button>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center border border-black/10 py-4">
            <div className="flex flex-col items-center gap-1"><Truck size={18} className="text-[#4A5D23]" /><span className="text-[10px] font-bold uppercase tracking-widest">Free Ship $75+</span></div>
            <div className="flex flex-col items-center gap-1"><RotateCcw size={18} className="text-[#4A5D23]" /><span className="text-[10px] font-bold uppercase tracking-widest">30-Day Returns</span></div>
            <div className="flex flex-col items-center gap-1"><ShieldCheck size={18} className="text-[#4A5D23]" /><span className="text-[10px] font-bold uppercase tracking-widest">Secure Checkout</span></div>
          </div>
          <div className="mt-4">
            <ShareButtons title={product.name} />
          </div>
        </div>
      </div>

      <div className="mt-14">
        <div className="flex border-b border-black/10 overflow-x-auto">
          {[["description", "Description"], ["features", "Features"], ["specs", "Specifications"], ["shipping", "Shipping & Returns"]].map(([val, l]) => (
            <button key={val} data-testid={`tab-${val}`} onClick={() => setTab(val)}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap transition-colors duration-150 ${tab === val ? "border-[#4A5D23] text-[#4A5D23]" : "border-transparent text-black/50 hover:text-black"}`}>{l}</button>
          ))}
        </div>
        <div className="py-6 text-sm text-black/70 max-w-3xl">
          {tab === "description" && <p>{product.description}</p>}
          {tab === "features" && (product.features?.length ? <ul className="space-y-2">{product.features.map((f) => <li key={f}>• {f}</li>)}</ul> : <p>See description.</p>)}
          {tab === "specs" && (
            <table className="w-full text-left">
              <tbody>{Object.entries(product.specs || {}).map(([k, val]) => (
                <tr key={k} className="border-b border-black/5"><td className="py-2 font-semibold pr-6 uppercase text-xs tracking-wider">{k}</td><td className="py-2">{val}</td></tr>
              ))}</tbody>
            </table>
          )}
          {tab === "shipping" && <p>Orders ship within 1-2 business days. Free standard shipping on qualifying orders over $75. 30-day hassle-free returns on unused items in original packaging. {product.has_restrictions ? "This product is subject to state and local shipping restrictions — check your local regulations before ordering." : "Some products are subject to state and local shipping restrictions — check your local regulations."}</p>}
        </div>
      </div>

      <Reviews slug={slug} onAggregate={() => api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => {})} />

      {related.length > 0 && (
        <div className="mt-14">
          <h2 className="font-heading text-2xl tracking-tight uppercase font-bold mb-2">Complete Your Kit</h2>
          <p className="text-sm text-black/50 mb-6">Frequently bought together with this item.</p>
          <div data-testid="related-products" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {related.slice(0, 4).map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
